import React, { useMemo } from 'react';
import { DollarSign, Building2, Calendar, TrendingUp } from 'lucide-react';

const PipelineView = ({ tasks, stages, onTaskClick, onTaskMove }) => {
  // Group tasks by stage
  const tasksByStage = useMemo(() => {
    const grouped = {};
    
    stages.forEach(stage => {
      grouped[stage.value] = tasks.filter(
        task => task.status.value === stage.value || task.status.label === stage.label
      );
    });
    
    return grouped;
  }, [tasks, stages]);

  // Calculate total value per stage
  const stageMetrics = useMemo(() => {
    const metrics = {};
    
    stages.forEach(stage => {
      const stageTasks = tasksByStage[stage.value] || [];
      const totalValue = stageTasks.reduce((sum, task) => sum + (task.deal_value || 0), 0);
      const count = stageTasks.length;
      
      metrics[stage.value] = { totalValue, count };
    });
    
    return metrics;
  }, [tasksByStage, stages]);

  const handleDragStart = (e, task) => {
    e.dataTransfer.setData('taskId', task._id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetStage) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    const task = tasks.find(t => t._id === taskId);
    
    if (task && task.status.value !== targetStage.value) {
      onTaskMove?.(taskId, targetStage);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStageColor = (stageValue) => {
    const colors = {
      'lead': { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700' },
      'contacted': { bg: 'bg-blue-100', border: 'border-blue-300', text: 'text-blue-700' },
      'proposal': { bg: 'bg-orange-100', border: 'border-orange-300', text: 'text-orange-700' },
      'negotiation': { bg: 'bg-purple-100', border: 'border-purple-300', text: 'text-purple-700' },
      'won': { bg: 'bg-green-100', border: 'border-green-300', text: 'text-green-700' },
      'lost': { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700' }
    };
    return colors[stageValue] || colors['lead'];
  };

  if (!tasks || tasks.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
        <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No deals in your pipeline</p>
        <p className="text-sm text-gray-500 mt-2">Add deals to start tracking your sales</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex gap-4 overflow-x-auto pb-4">
        {stages.map((stage) => {
          const stageTasks = tasksByStage[stage.value] || [];
          const metrics = stageMetrics[stage.value] || { totalValue: 0, count: 0 };
          const colors = getStageColor(stage.value);

          return (
            <div
              key={stage.value}
              className="flex-shrink-0 w-80"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, stage)}
            >
              {/* Stage Header */}
              <div className={`${colors.bg} ${colors.border} border-2 rounded-t-lg p-4`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`font-semibold ${colors.text}`}>
                    {stage.label}
                  </h3>
                  <span className={`text-xs ${colors.text} bg-white/50 px-2 py-1 rounded-full`}>
                    {metrics.count}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className={`h-4 w-4 ${colors.text}`} />
                  <span className={`font-bold ${colors.text}`}>
                    {formatCurrency(metrics.totalValue)}
                  </span>
                </div>
              </div>

              {/* Stage Body with Tasks */}
              <div
                className={`bg-white ${colors.border} border-2 border-t-0 rounded-b-lg p-2 min-h-[400px] max-h-[600px] overflow-y-auto`}
              >
                <div className="space-y-2">
                  {stageTasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-sm">
                      No deals yet
                    </div>
                  ) : (
                    stageTasks.map((task) => (
                      <div
                        key={task._id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, task)}
                        onClick={() => onTaskClick?.(task)}
                        className="bg-white border-2 border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-[#1C3E53] transition-all cursor-move"
                      >
                        {/* Task Title */}
                        <h4 className="font-semibold text-gray-900 mb-2 text-sm">
                          {task.task}
                        </h4>

                        {/* Deal Value */}
                        {task.deal_value && (
                          <div className="flex items-center gap-2 mb-2">
                            <DollarSign className="h-4 w-4 text-green-600" />
                            <span className="font-bold text-green-600">
                              {formatCurrency(task.deal_value)}
                            </span>
                          </div>
                        )}

                        {/* Company */}
                        {task.company && (
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="h-3 w-3 text-gray-500" />
                            <span className="text-xs text-gray-600">
                              {task.company}
                            </span>
                          </div>
                        )}

                        {/* Closing Date */}
                        {task.closing_date && (
                          <div className="flex items-center gap-2 mb-2">
                            <Calendar className="h-3 w-3 text-gray-500" />
                            <span className="text-xs text-gray-600">
                              {new Date(task.closing_date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                        )}

                        {/* Assignee */}
                        {task.person && (
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-xs text-white font-semibold"
                              style={{ backgroundColor: task.person.color || '#999' }}
                            >
                              {task.person.avatar}
                            </div>
                            <span className="text-xs text-gray-600">
                              {task.person.name}
                            </span>
                          </div>
                        )}

                        {/* Next Action */}
                        {task.next_action && (
                          <div className="mt-2 pt-2 border-t">
                            <p className="text-xs text-gray-500 italic">
                              Next: {task.next_action}
                            </p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Footer */}
      <div className="mt-4 bg-white rounded-lg border p-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Total Pipeline</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(
                Object.values(stageMetrics).reduce((sum, m) => sum + m.totalValue, 0)
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {tasks.length} deals
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Won Deals</div>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(stageMetrics['won']?.totalValue || 0)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {stageMetrics['won']?.count || 0} deals
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500 mb-1">Conversion Rate</div>
            <div className="text-2xl font-bold text-purple-600">
              {tasks.length > 0
                ? Math.round(((stageMetrics['won']?.count || 0) / tasks.length) * 100)
                : 0}%
            </div>
            <div className="text-xs text-gray-500 mt-1">
              lead to close
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipelineView;
